import { useDispatch } from 'react-redux'
import { useEffect, useState } from 'react'
import useOrder from '../../modules/order/state/order/use-order'
import useConfig from '../../modules/config/state/use-config'
import { trackBeginCheckoutEvent } from '../../modules/analytics/helpers/track-begin-checkout-event'
import { useInternalAnalytics } from 'utils/internalAnalytics'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import { useFeatureFlag } from 'modules/optimizely/hooks/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'models/Optimizely'
import { useEffectOnceWhenReady } from './generalUtilHooks'

/**
 * Triggers analytics when the page is loaded and the app is visible.
 *
 * @param isAppVisible - Boolean flag indicating whether the app is visible to the user
 */
const usePageLoadAnalytics = (isReady: boolean) => {
  const config = useConfig()
  const { amount, currency, frequency } = useOrder()
  const dispatch = useDispatch()

  useEffectOnceWhenReady(() => {
    trackBeginCheckoutEvent({
      currency: currency.value ?? 'USD',
      frequency: frequency.value,
      value: Number(amount.value),
      campaignId: config.campaignId,
      campaignName: config.campaignName,
      organizationId: config.organization.id,
      organizationName: config.organization.name,
      designationId: config.designation.designationId,
      dispatch,
    })
  }, [
    isReady, // Only trigger the analytics if the app is visible,
  ])

  // Get the mParticle feature flag from Optimizely context
  const isMParticleEnabled = useFeatureFlag(OptimizelyFeatureFlags.classy_mparticle).enabled
  const { mParticle, consentSynced } = useInternalAnalytics()

  useEffect(() => {
    if (
      !isMParticleEnabled ||
      !mParticle ||
      !consentSynced ||
      !config.campaignName ||
      !frequency ||
      !currency
    )
      return

    mParticle.logPageView('Checkout Page', {
      currency: currency.value ?? 'USD',
      frequency: frequency.value,
      value: Number(amount),
      campaign_name: config.campaignName,
      app: 'classy-checkout',
      route: window.location.href,
    })

    mParticle.Identity.login({
      userIdentities: {
        other3: getClassySessionId(),
      },
    })
  }, [
    isMParticleEnabled,
    mParticle,
    consentSynced,
    amount,
    config.campaignName,
    frequency,
    currency,
  ])
}

export default usePageLoadAnalytics
